import type { ComponentType } from "react";
import type { StatusStep } from "./advisoryApi";
import { reasonText } from "./format";
import type { NavItem } from "./nav";
import { itemAddr } from "./nav";
import { AdvisorNoteStep } from "./steps/AdvisorNoteStep";
import { CustomPortfolioStep } from "./steps/CustomPortfolioStep";
import { FieldsStep } from "./steps/FieldsStep";
import { FinancialSituationStep } from "./steps/FinancialSituationStep";
import { GoalStep } from "./steps/GoalStep";
import { KnowledgeStep } from "./steps/KnowledgeStep";
import { AmlStep, ClassificationStep, GenericStep, PreferencesStep, SustainabilityStatusStep } from "./steps/OtherSteps";
import { PortfolioStep } from "./steps/PortfolioStep";
import { ResultsStep } from "./steps/ResultsStep";
import { RiskStep } from "./steps/RiskStep";
import { SustainabilityStep } from "./steps/SustainabilityStep";
import type { StepProps } from "./steps/common";

// componentType → desktop renderer (§6). Display snapshots share one renderer
// per page (QP shows them as one tabs block); fields share one per page too.
const REGISTRY: Record<string, ComponentType<StepProps>> = {
  goal_input: GoalStep,
  risk_assessment: RiskStep,
  portfolio_input: PortfolioStep,
  custom_portfolio: CustomPortfolioStep,
  financial_situation: FinancialSituationStep,
  product_assessment: KnowledgeStep,
  sustainability: SustainabilityStep,
  sustainability_status: SustainabilityStatusStep,
  client_classification: ClassificationStep,
  client_preferences: PreferencesStep,
  trapets_lookup: AmlStep,
  enin_lookup: AmlStep,
};

/** Which prefill (if any) the right column offers for a step, and its step key. */
export const PREFILL_KEY: Record<string, string> = {
  financial_situation: "financial-situation",
  risk_assessment: "risk-assessment",
  portfolio_input: "portfolio-input",
};

interface Block {
  key: string;
  kind: "fields" | "snapshots" | "component" | "note";
  componentType?: string;
  steps: StatusStep[];
}

/** Split a page's status rows into renderer blocks, keeping QP's order. */
function blocks(steps: StatusStep[]): Block[] {
  const out: Block[] = [];
  const find = (k: string) => out.find((b) => b.key === k);
  for (const s of steps) {
    if (s.kind === "field") {
      const b = find("fields") ?? (out.push({ key: "fields", kind: "fields", steps: [] }), out[out.length - 1]!);
      b.steps.push(s);
    } else if (s.kind === "snapshot") {
      const b = find("snapshots") ?? (out.push({ key: "snapshots", kind: "snapshots", steps: [] }), out[out.length - 1]!);
      b.steps.push(s);
    } else if (s.kind === "advisor_note") {
      out.push({ key: `note:${out.length}`, kind: "note", steps: [s] });
    } else {
      const ct = s.componentType ?? s.kind;
      const k = `c:${ct}`;
      const b = find(k) ?? (out.push({ key: k, kind: "component", componentType: ct, steps: [] }), out[out.length - 1]!);
      b.steps.push(s);
    }
  }
  return out;
}

/**
 * One navigator item's page: its renderers in QP's order, then the page's
 * advisor note (optional unless the page requires one). Every renderer saves
 * explicitly and hands the fresh /status back to the workbench.
 */
export function StepPage({
  item,
  base,
  attempted,
  prefillFor,
}: {
  item: NavItem;
  base: Omit<StepProps, "steps" | "addr" | "prefill">;
  attempted: boolean;
  prefillFor: (componentType: string) => StepProps["prefill"];
}) {
  const addr = itemAddr(item);
  const list = blocks(item.steps);
  const noteRequired = list.some((b) => b.kind === "note");
  const openSteps = item.steps.filter((s) => s.status === "open");

  return (
    <div className="adv-page-body">
      {attempted && openSteps.length > 0 && (
        <ul className="check-list adv-missing">
          {openSteps.map((s, i) => (
            <li key={s.componentId ?? s.fieldId ?? i} className="check-row check-block">
              <span className="check-icon">!</span>
              <span>
                <strong>Still needed</strong> — {reasonText(s.reason) ?? "complete this before submitting."}
              </span>
            </li>
          ))}
        </ul>
      )}
      {list.map((b) => {
        if (b.kind === "note") return null; // rendered once, below
        const props: StepProps = { ...base, steps: b.steps, addr, prefill: null };
        if (b.kind === "fields") return <FieldsStep key={b.key} {...props} />;
        if (b.kind === "snapshots") return <ResultsStep key={b.key} {...props} />;
        const Renderer = REGISTRY[b.componentType ?? ""] ?? GenericStep;
        return <Renderer key={`${b.key}:${addr.goalId ?? ""}:${addr.portfolioId ?? ""}`} {...props} prefill={prefillFor(b.componentType ?? "")} />;
      })}
      <AdvisorNoteStep
        key={`note:${addr.pageId}:${addr.goalId ?? ""}`}
        sessionId={base.sessionId}
        addr={addr}
        readOnly={base.readOnly}
        onSaved={base.onSaved}
        required={noteRequired}
      />
    </div>
  );
}
